import React from 'react';
import PropTypes from 'prop-types';
import { Box, DataTable, Spinner, Text, ResponsiveContext } from 'wt-ui';

import i18next from '../../translations/i18n';

import HeaderWithDrop from './HeaderWithDrop';
import TextHeaderWithDrop from './TextHeaderWithDrop';
import TableHeaderText from './TableHeaderText';
import TableFooterText from './TableFooterText';
import MobileTable from './MobileTable';

import DynamicText from '../../services/DynamicText';

const getFullIndexName = fundData => {
  if (fundData.relatedIndex === fundData.indexTR) {
    return fundData.IndexNameTR;
  }
  if (fundData.relatedIndex === fundData.indexER) {
    return fundData.IndexNameER;
  }
  return '';
};

const shortStrikeTime = strikeTime =>
  strikeTime
    .replace('New York Time', 'EST')
    .replace('London Time', 'LDN')
    .replace('Central US Time', 'CST')
    .replace('Central European Time', 'CET');

const formatDate = date => {
  if (date === 'Period Return') {
    return date;
  }
  const dt = typeof date === 'string' ? new Date(date) : date;
  let month = dt.getMonth() + 1;
  month = month < 10 ? `0${month}` : month;

  let day = dt.getDate();
  day = day < 10 ? `0${day}` : day;

  return `${day}/${month}/${dt.getFullYear()}`;
};

const columns = (fundData, size) => {
  const ticker = (
    fundData.bloombergTickerPrimary || fundData.bloombergTicker
  ).split(' ')[0];
  const c = [
    {
      property: 'dt',
      header: (
        <Box align="center">
          <Text color="#fff">{i18next.t('Date')}</Text>
        </Box>
      ),
      primary: true,
      render: datum => formatDate(datum.dt),
    },
  ];

  if (size !== 'small') {
    c.push({
      property: 'StrikeTime',
      header: (
        <HeaderWithDrop
          text={i18next.t('StrikeTime')}
          dropText={DynamicText.replaceText(
            i18next.t('helpText_StrikeTime'),
            fundData
          )}
        />
      ),
      render: () => (
        <Box align="center" background="transparent">
          <Text>
            {!fundData.StrikeTime || fundData.StrikeTime === ''
              ? ''
              : shortStrikeTime(fundData.StrikeTime)}
          </Text>
        </Box>
      ),
      primary: true,
    });
  }

  c.push(
    {
      property: 'Fund',
      header: (
        <TextHeaderWithDrop
          text={
            <Box align="center">
              <Text color="#fff">{ticker}</Text>
              <Text color="#fff">(NAV)</Text>
            </Box>
          }
          dropText={fundData.displayName}
        />
      ),
      render: datum => (
        <Box align="center" background="transparent">
          <Text>{datum.Fund}</Text>
        </Box>
      ),
      primary: true,
    },
    {
      property: 'Index',
      header: (
        <TextHeaderWithDrop
          text={
            <Box align="center">
              <Text color="#fff">{fundData.relatedIndex}</Text>
              <Text color="#fff">({i18next.t('Index rebased to 100')})</Text>
            </Box>
          }
          dropText={getFullIndexName(fundData)}
        />
      ),
      render: datum => (
        <Box align="center" background="transparent">
          <Text>{datum.Index}</Text>
        </Box>
      ),
      primary: true,
    },
    {
      property: 'fundPct',
      header: (
        <Box align="center">
          <Text color="#fff">{ticker}</Text>
          <Text color="#fff">({i18next.t('Daily % return')})</Text>
        </Box>
      ),
      render: datum => (
        <Box align="center" background="transparent">
          <Text>{datum.fundPct ? `${datum.fundPct} %` : ''}</Text>
        </Box>
      ),
      primary: true,
    },
    {
      property: 'indexPct',
      header: (
        <Box align="center">
          <Text color="#fff">{fundData.relatedIndex}</Text>
          <Text color="#fff">({i18next.t('Daily % return')})</Text>
        </Box>
      ),
      render: datum => (
        <Box align="center" background="transparent">
          <Text>{datum.indexPct ? `${datum.indexPct} %` : ''}</Text>
        </Box>
      ),
      primary: true,
    }
  );
  if (fundData.isSnL) {
    c.push({
      property: 'leverage',
      header: (
        <HeaderWithDrop
          text={i18next.t('Implied Leverage')}
          dropText={DynamicText.replaceText(
            i18next.t('helpText_ImpliedLeverage'),
            fundData
          )}
        />
      ),
      render: datum => (
        <Box align="center" background="transparent">
          <Text>{datum.impliedLeverage || ''}</Text>
        </Box>
      ),
      primary: true,
    });
  }
  return c;
};

function Table({ data, loading, fundData, filters }) {
  if (loading) {
    return (
      <Box background="transparent" align="center">
        <Spinner primary />
      </Box>
    );
  }

  if (!fundData) {
    return (
      <Box background="transparent" align="center">
        <Text>{`${i18next.t(
          'Select a product to see performance breakdown'
        )}.`}</Text>
      </Box>
    );
  }

  if (fundData !== null && data.length <= 0) {
    return (
      <Box background="transparent" align="center">
        <Text>{`${i18next.t(
          'Performance data for this product is currently not available. If you believe this to be an error please contact us.'
        )}.`}</Text>
      </Box>
    );
  }

  return (
    <>
      <Box background="transparent" align="center" gap="medium">
        <TableHeaderText fundData={fundData} filters={filters} />

        <ResponsiveContext.Consumer>
          {size => {
            if (size === 'small') {
              return (
                <MobileTable columns={columns(fundData, size)} data={data} />
              );
            }
            return (
              <DataTable
                columns={columns(fundData, size)}
                data={data}
                fullwidth
              />
            );
          }}
        </ResponsiveContext.Consumer>
        <TableFooterText fundData={fundData} />
      </Box>
    </>
  );
}

Table.defaultProps = {
  fundData: null,
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loading: PropTypes.bool.isRequired,
  fundData: PropTypes.shape({}),
  filters: PropTypes.shape({}).isRequired,
};

export default Table;
